import React, { Fragment } from 'react';

import { useEnumPreload } from '../../hooks/useEnum';
import RouteEnum from '../../RouteEnum';
import { useRouteContext } from '../../RouteProvider';
import type { AppState } from '../../services/AppState';
import type { Pageview } from '../../services/Pageview';
import type { EnumsStore } from '../../store/enums';
import Loader from '../Loader';
import MetaTags from '../MetaTags';
import Modals from '../Modals';
import NotFound from '../NotFound';
import Redirect from '../Redirect';
import { Restriction, type RestrictionAttributes } from '../Restriction';
import { PageView } from '../Tracking';

import FlashMessage from './FlashMessage';
import Footer from './Footer';
import Navbar from './Navbar';
import Private from './Private';
import TopBanner from './TopBanner';

export interface PageProps {
  requiredState?: AppState;
  hideNavbar?: boolean;
  hideFooter?: boolean;
  hideTopBanner?: boolean;
  private?: boolean;
  maySkipBlocker?: boolean;
  restriction?: RestrictionAttributes;
  requiredEnums?: (keyof EnumsStore)[];
  preloadEnums?: (keyof EnumsStore)[];
  pageview?: Pageview;
  customNavbar?: React.ReactNode;
  hideMeta?: boolean;
}

function LoadingPage({ hideMeta = false }: { hideMeta?: boolean }) {
  return (
    <main className="container main-container container--top container--bottom">
      {!hideMeta && <MetaTags />}
      <Loader />
    </main>
  );
}

export default function PageLayout(props: React.PropsWithChildren<PageProps>) {
  const { pathWithoutPrefix } = useRouteContext();
  const restrictBlockedApp =
    pathWithoutPrefix.replace(/\/$/, '') !== RouteEnum.USER_REGISTRATION && !props.maySkipBlocker;
  useEnumPreload(props.preloadEnums);
  const loaded = useEnumPreload(props.requiredEnums);

  let Component: React.ElementType = Fragment;
  if (props.private) {
    Component = Private;
  }

  return (
    <>
      <div id="app">
        <FlashMessage />
        {process.env.GATSBY_SHOW_TOPBANNER === 'true' && <TopBanner hide={props.hideTopBanner} />}
        <div className="home">
          {!props.hideNavbar && (props.customNavbar ?? <Navbar />)}
          <Restriction
            appBlocked={restrictBlockedApp ? true : undefined}
            fallback={<Redirect to={RouteEnum.USER_REGISTRATION} />}
          >
            <Restriction {...props.restriction} fallback={<NotFound />}>
              <Restriction fallback={<LoadingPage hideMeta={props.hideMeta} />} minAppState={props.requiredState}>
                {loaded ? (
                  <Component>
                    <PageView name={props.pageview} />
                    {props.children}
                  </Component>
                ) : (
                  <LoadingPage hideMeta={props.hideMeta} />
                )}
              </Restriction>
            </Restriction>
          </Restriction>
          {!props.hideFooter && <Footer />}
        </div>
      </div>
      <div id="modal">
        <Modals />
      </div>
    </>
  );
}
